import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Settings, Calendar, Users, Filter, Save, Eye, Trash2, Edit3, ChevronUp, ChevronDown, TableProperties, ArrowLeft, Mail, ChevronsUpDown, Link2, ExternalLink, Copy } from 'lucide-react';
import Modal from './Modal';
import '../styles/Homepage.css';
import '../styles/Reports.css';
import { Checkbox, SearchSelect, DateInput, Dropdown } from './FieldComponents';
import { useNavVariant } from '../App';
import { fetchNavSearchResults, toCaseSelectionOptions } from '../utils/navSearch';

// Helper functions for column width and formatting (similar to ReportView)
const getColumnWidth = (column, docketCases) => {
    if (!docketCases || !docketCases.length) return '120px';
    
    const fieldId = column.field_id;
    const fieldName = column.label;
    
    // Get max content length for this column
    const maxContentLength = Math.max(
        fieldName.length,
        ...docketCases.map(case_ => {
            const value = getCellValue(case_, column);
            const displayValue = formatCellValue(value, column.type);
            return String(displayValue || '').length;
        })
    );
    
    // Base width calculation with generous spacing
    let width = Math.max(80, Math.min(450, maxContentLength * 10 + 50));
    
    // For very long content (>40 characters), be more generous
    if (maxContentLength > 40) {
        width = Math.max(width, maxContentLength * 11 + 60);
        width = Math.min(width, 500); // Cap at 500px for very long content
    }
    
    // Adjust for specific field types
    if (column.type === 'currency') width = Math.max(width, 120);
    if (column.type === 'date') width = Math.max(width, 100);
    
    // Special handling for case names and long text
    if (fieldId === 'case_name' || fieldName.toLowerCase().includes('name')) {
        width = Math.max(width, 200);
    }
    
    return `${width}px`;
};

const getTextAlign = (column) => {
    if (column.type === 'currency') return 'right';
    if (column.type === 'date') return 'center';
    return 'left';
};

const getCellValue = (case_, column) => {
    const fieldId = typeof column === 'string' ? column : column.field_id;
    
    if (fieldId === 'case_name') return case_.case_name;
    if (fieldId === 'first_primary_user_name') return case_.first_primary_user_name || 'N/A';
    if (fieldId === 'contact_name') return case_.contact_name || 'N/A';
    if (fieldId === 'phase_name') return case_.phase_name || 'N/A';
    if (fieldId === 'create_date') return case_.create_date;
    if (fieldId === 'created_at') return case_.created_at;
    
    // Don't use || operator which converts falsy values like 0 to empty string
    const value = case_.fields?.[fieldId];
    return value !== undefined ? value : '';
};

const formatCellValue = (value, type) => {
    if (value === null || value === undefined) return '';
    if (value === '' && type !== 'currency' && type !== 'number') return '';
    
    const stringValue = String(value);
    
    // Currency formatting
    if (type === 'currency') {
        const numValue = parseFloat(stringValue.replace(/[^0-9.-]/g, ''));
        if (!isNaN(numValue)) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD'
            }).format(numValue);
        }
    }
    
    // Date formatting
    if (type === 'date') {
        if (stringValue && /^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
            try {
                // Parse date without timezone conversion by treating as local date
                const [year, month, day] = stringValue.split('-').map(Number);
                const date = new Date(year, month - 1, day);
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                });
            } catch {
                return stringValue;
            }
        }
    }
    
    // Handle general date fields
    if (stringValue.includes('-') && stringValue.length >= 8 && /^\d{4}-\d{2}-\d{2}/.test(stringValue)) {
        try {
            // Parse date without timezone conversion by treating as local date
            const [year, month, day] = stringValue.split('-').map(Number);
            const date = new Date(year, month - 1, day);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return stringValue;
        }
    }
    
    // Default: clean up comma spacing
    return stringValue.replace(/,\s*/g, ', ');
};

const hasMeaningfulValue = (value) => value !== null && value !== undefined && value !== '';

const caseMatchesDocketFilters = (case_, config, overrides = {}) => {
    const fieldConditions = config?.filters?.field_conditions || [];
    if (!fieldConditions.length) return true;

    return fieldConditions.every((condition) => {
        const fieldId = condition?.field_id;
        if (fieldId === undefined || fieldId === null) return true;

        const conditionType = condition?.condition || 'empty';
        const currentValue = Object.prototype.hasOwnProperty.call(overrides, fieldId)
            ? overrides[fieldId]
            : case_?.fields?.[fieldId];

        if (fieldId === 194) {
            if (conditionType === 'empty') return !hasMeaningfulValue(currentValue);
            if (conditionType === 'not_empty') return hasMeaningfulValue(currentValue);
        }

        if (conditionType === 'empty') {
            return !hasMeaningfulValue(currentValue);
        }

        if (conditionType === 'not_empty') {
            return hasMeaningfulValue(currentValue);
        }

        if (conditionType === 'equals') {
            return String(currentValue ?? '') === String(condition?.value ?? '');
        }

        if (conditionType === 'contains') {
            return String(currentValue ?? '').toLowerCase().includes(String(condition?.value ?? '').toLowerCase());
        }

        return true;
    });
};

const getSortIcon = (column, sortConfig) => {
    if (sortConfig?.key !== column.field_id) return <ChevronsUpDown size={14} />;
    return sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
};

const getTotalTableWidth = (columns, docketCases, showCheckboxColumn = false) => {
    if (!columns.length) return 'auto';
    
    let totalWidth = columns.reduce((sum, column) => {
        const width = parseInt(getColumnWidth(column, docketCases));
        return sum + width;
    }, 0);
    
    if (showCheckboxColumn) {
        totalWidth += 60; // 60px for checkbox column
    }

    return `${totalWidth}px`;
};

const ConfigurationCard = ({ config, onSelect }) => (
    <div className="case-card" onClick={() => onSelect(config.id)}>
        <div className="case-card-content">
            <h4>{config.name}</h4>
            <div className="case-card-subtitle">By {config.created_by_name}</div>
        </div>
        <p style={{ color: 'var(--subtext-color)', margin: '0 0 15px 0' }}>{config.description}</p>
        <div style={{ display: 'flex', gap: '15px', fontSize: '14px', color: 'var(--subtext-color)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Filter size={14} />
                <span>{config.config.filters.field_conditions?.length || 0} filters</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <TableProperties size={14} />
                <span>{config.config.columns?.length || 0} columns</span>
            </div>
            {config.config.notifications?.enabled && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Calendar size={14} />
                    <span>Weekly notifications</span>
                </div>
            )}
        </div>
    </div>
);

export const Docket = () => {
    const [configurations, setConfigurations] = useState([]);
    const [currentConfig, setCurrentConfig] = useState(null);
    const [cases, setCases] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [pagination, setPagination] = useState({
        current_page: 1,
        per_page: 250,
        total_cases: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false
    });
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [editingCell, setEditingCell] = useState(null);
    const [fieldOptions, setFieldOptions] = useState({});
    const navVariant = useNavVariant();
    
    // Case restriction feature
    const [showRestrictCases, setShowRestrictCases] = useState(false);
    const [availableCases, setAvailableCases] = useState({});
    const [restrictedCaseIds, setRestrictedCaseIds] = useState([]);
    const [selectedCasesForRestriction, setSelectedCasesForRestriction] = useState([]);
    
    // Townhall feature
    const [showTownhallModal, setShowTownhallModal] = useState(false);
    const [townhallLink, setTownhallLink] = useState('');
    const [editingTownhallLink, setEditingTownhallLink] = useState('');
    const [townhallPasscode, setTownhallPasscode] = useState('');
    const [editingTownhallPasscode, setEditingTownhallPasscode] = useState('');

    // Form state for creating new docket configuration
    const [newConfig, setNewConfig] = useState({
        name: '',
        description: '',
        template: 'custom', // Default template
        config: {
            filters: {
                team_position: 'any_team_member',
                field_conditions: [
                    { field_id: 194, condition: 'empty', value: null }
                ],
                case_status: 'active'
            },
            columns: [
                { field_id: 'case_name', label: 'Case Name', type: 'text', editable: false },
                { field_id: 'first_primary_user_name', label: 'Primary Attorney', type: 'text', editable: false },
                { field_id: 194, label: 'Projected Settlement', type: 'currency', editable: true },
                { field_id: 'create_date', label: 'Created Date', type: 'date', editable: false }
            ],
            notifications: {
                enabled: true,
                frequency: 'weekly',
                day_of_week: 1,
                recipients: 'auto'
            }
        }
    });

    // Docket templates for easy creation
    const docketTemplates = {
        settlement_review: {
            name: 'Settlement Review Docket',
            description: 'Cases that need projected settlement values',
            filters: {
                team_position: 'first_primary',
                field_conditions: [{ field_id: 194, condition: 'empty', value: null }],
                case_status: 'active'
            },
            columns: [
                { field_id: 'case_name', label: 'Case Name', type: 'text', editable: false },
                { field_id: 'first_primary_user_name', label: 'Primary Attorney', type: 'text', editable: false },
                { field_id: 194, label: 'Projected Settlement', type: 'currency', editable: true },
                { field_id: 'create_date', label: 'Created Date', type: 'date', editable: false }
            ]
        },
        case_vitals: {
            name: 'Case Vitals Review',
            description: 'All cases where I am on the team - basic case information',
            filters: {
                team_position: 'any_team_member',
                field_conditions: [],
                case_status: 'active'
            },
            columns: [
                { field_id: 'case_name', label: 'Case Name', type: 'text', editable: false },
                { field_id: 'first_primary_user_name', label: 'Primary Attorney', type: 'text', editable: false },
                { field_id: 'create_date', label: 'Created Date', type: 'date', editable: false },
                { field_id: 'contact_name', label: 'Client Name', type: 'text', editable: false },
                { field_id: 'phase_name', label: 'Current Phase', type: 'text', editable: false }
            ]
        },
        custom: {
            name: '',
            description: '',
            filters: {
                team_position: 'first_primary',
                field_conditions: [],
                case_status: 'active'
            },
            columns: [
                { field_id: 'case_name', label: 'Case Name', type: 'text', editable: false },
                { field_id: 'first_primary_user_name', label: 'Primary Attorney', type: 'text', editable: false },
                { field_id: 'create_date', label: 'Created Date', type: 'date', editable: false }
            ]
        }
    };

    useEffect(() => {
        fetchCurrentUser();
        fetchConfigurations();

        document.title = "Docket | CaseDB";
        
        // Check if there's a docket ID in the URL path
        const pathParts = window.location.pathname.split('/');
        const docketIndex = pathParts.indexOf('docket');
        if (docketIndex !== -1 && pathParts[docketIndex + 1]) {
            const configId = parseInt(pathParts[docketIndex + 1]);
            if (!isNaN(configId)) {
                // Load this specific docket configuration
                setTimeout(() => {
                    fetchDocketData(configId);
                }, 100); // Small delay to ensure configurations are loaded
            }
        }
    }, []);

    const fetchCurrentUser = async () => {
        try {
            // For development: use hardcoded user data on localhost
            if (window.location.hostname === 'localhost') {
                const devUserData = {
                    isLoggedIn: true,
                    user_id: 12,
                    name: "Tyler Nolet",
                    access_level: "global admin",
                    timekeeper: 1
                };
                
                setCurrentUser({
                    id: devUserData.user_id,
                    name: devUserData.name,
                    access_level: devUserData.access_level,
                    timekeeper: devUserData.timekeeper
                });
                console.log('Development user loaded:', {
                    id: devUserData.user_id,
                    name: devUserData.name,
                    access_level: devUserData.access_level
                });
                return;
            }
            
            // Production: fetch from session.php
            const response = await fetch('https://api.casedb.co/session.php', { credentials: 'include' });
            const data = await response.json();
            if (data.isLoggedIn && data.user_id) {
                setCurrentUser({
                    id: data.user_id,
                    name: data.name,
                    access_level: data.access_level,
                    timekeeper: data.timekeeper
                });
                console.log('Current user loaded:', {
                    id: data.user_id,
                    name: data.name,
                    access_level: data.access_level
                });
            } else {
                console.log('No user session found');
            }
        } catch (error) {
            console.error('Error fetching current user:', error);
        }
    };

    const fetchConfigurations = async () => {
        try {
            setLoading(true);
            const response = await fetch(`https://api.casedb.co/docket.php?list_configs=true&t=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setConfigurations(data.configurations);
            }
        } catch (error) {
            console.error('Error fetching configurations:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchDocketData = async (configId, page = 1) => {
        try {
            setLoading(true);
            
            // Extract user parameter from URL if present
            const urlParams = new URLSearchParams(window.location.search);
            const userParam = urlParams.get('user');
            
            // Build API URL with user parameter if present
            let apiUrl = `https://api.casedb.co/docket.php?config_id=${configId}&page=${page}&limit=250&t=${new Date().getTime()}`;
            if (userParam) {
                apiUrl += `&user=${encodeURIComponent(userParam)}`;
                console.log(`[DOCKET FRONTEND] Requesting docket for user ${userParam}`);
            }
            
            const response = await fetch(apiUrl, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                setCurrentConfig(data.configuration);
                setFieldOptions(data.field_options || {});
                
                // Debug: Check if field options are being loaded
                console.log('Docket configuration loaded:', data.configuration);
                console.log('Field options loaded:', data.field_options);
                console.log('Column options:', data.configuration?.config?.columns?.map(col => ({
                    field_id: col.field_id,
                    type: col.type,
                    options: col.options,
                    hasOptions: !!col.options
                })));
                
                setCases(data.cases);
                setPagination(data.pagination || {
                    current_page: page,
                    per_page: 250,
                    total_cases: data.cases.length,
                    total_pages: 1,
                    has_next: false,
                    has_prev: false
                });
                // Load restricted case IDs from configuration
                setRestrictedCaseIds(data.configuration?.restricted_case_ids || []);
                // Load townhall link from configuration
                setTownhallLink(data.configuration?.townhall || '');
                // Load townhall passcode from configuration
                setTownhallPasscode(data.configuration?.passcode || '');
            }
        } catch (error) {
            console.error('Error fetching docket data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCaseOptions = useCallback(async (query = '') => {
        const results = await fetchNavSearchResults(query, { type: 'cases', limit: 15 });
        return toCaseSelectionOptions(results);
    }, []);

    useEffect(() => {
        const hydrateRestrictedCaseLabels = async () => {
            if (!restrictedCaseIds.length) {
                return;
            }

            const missingCaseIds = restrictedCaseIds.filter(caseId => !availableCases[caseId]);
            if (!missingCaseIds.length) {
                return;
            }

            try {
                const results = await Promise.all(
                    missingCaseIds.map(async (caseId) => {
                        const searchResults = await fetchNavSearchResults(String(caseId), { type: 'cases', limit: 10 });
                        const exactMatch = (searchResults.cases || []).find(item => Number(item.value) === Number(caseId));
                        return exactMatch ? [caseId, exactMatch.label] : [caseId, `Case #${caseId}`];
                    })
                );

                setAvailableCases((prev) => ({
                    ...prev,
                    ...Object.fromEntries(results)
                }));
            } catch (error) {
                console.error('Error loading restricted case labels:', error);
            }
        };

        hydrateRestrictedCaseLabels();
    }, [availableCases, restrictedCaseIds]);

    const previewCases = async (config) => {
        try {
            setLoading(true);
            const response = await fetch(`https://api.casedb.co/docket.php?preview_cases=true&config=${encodeURIComponent(JSON.stringify(config))}&t=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (data.success) {
                return data.cases;
            }
            return [];
        } catch (error) {
            console.error('Error previewing cases:', error);
            return [];
        } finally {
            setLoading(false);
        }
    };

    const createConfiguration = async () => {
        try {
            setLoading(true);
            const response = await fetch('https://api.casedb.co/docket.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'create_config',
                    name: newConfig.name,
                    description: newConfig.description,
                    config: newConfig.config
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setShowCreateModal(false);
                fetchConfigurations();
                // Load the newly created configuration
                fetchDocketData(data.config_id);
            } else {
                alert('Error creating configuration: ' + data.message);
            }
        } catch (error) {
            console.error('Error creating configuration:', error);
            alert('Error creating configuration');
        } finally {
            setLoading(false);
        }
    };

    const updateFieldValue = async (caseId, fieldId, value) => {
        try {
            const response = await fetch('https://api.casedb.co/docket.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'update_field',
                    case_id: caseId,
                    field_id: fieldId,
                    value: value,
                    config_id: currentConfig.id
                })
            });
            
            const data = await response.json();
            if (data.success) {
                const filterFields = currentConfig?.config?.filters?.field_conditions || [];
                const fieldTouchesActiveFilter = filterFields.some(condition => Number(condition?.field_id) === Number(fieldId));

                setCases(prevCases => {
                    const nextCases = prevCases.filter(case_ => case_.id !== caseId || !fieldTouchesActiveFilter);
                    if (nextCases.length === prevCases.length) {
                        return prevCases.map(case_ => {
                            if (case_.id !== caseId) return case_;

                            let displayValue = value;
                            const column = currentConfig?.config?.columns?.find(col => col.field_id == fieldId);
                            if (column && (column.type === 'dropdown' || column.type === 'list')) {
                                const options = fieldOptions[fieldId] || [];
                                if (typeof value === 'number' && options[value]) {
                                    displayValue = options[value];
                                }
                            }

                            return {
                                ...case_,
                                fields: {
                                    ...case_.fields,
                                    [fieldId]: displayValue
                                }
                            };
                        });
                    }

                    setPagination(prev => ({
                        ...prev,
                        total_cases: Math.max(0, (prev.total_cases || 0) - 1)
                    }));
                    return nextCases;
                });
            } else {
                alert('Error updating field: ' + data.message);
            }
        } catch (error) {
            console.error('Error updating field:', error);
            alert('Error updating field');
        }
    };

    const addRestrictedCase = async (selectedCaseId) => {
        if (!selectedCaseId || !currentConfig) return;
        
        // Check if case is already restricted
        if (restrictedCaseIds.includes(parseInt(selectedCaseId))) {
            alert('This case is already restricted.');
            return;
        }
        
        const newRestrictedIds = [...restrictedCaseIds, parseInt(selectedCaseId)];
        
        try {
            setLoading(true);
            const response = await fetch('https://api.casedb.co/docket.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'update_restricted_cases',
                    config_id: currentConfig.id,
                    restricted_case_ids: newRestrictedIds
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setRestrictedCaseIds(newRestrictedIds);
                // Refresh the docket data to reflect the restriction
                fetchDocketData(currentConfig.id, pagination.current_page);
            } else {
                alert('Error restricting case: ' + data.message);
            }
        } catch (error) {
            console.error('Error restricting case:', error);
            alert('Error restricting case');
        } finally {
            setLoading(false);
        }
    };

    const removeRestrictedCase = async (caseIdToRemove) => {
        if (!currentConfig) return;
        
        const newRestrictedIds = restrictedCaseIds.filter(id => id !== caseIdToRemove);
        
        try {
            setLoading(true);
            const response = await fetch('https://api.casedb.co/docket.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'update_restricted_cases',
                    config_id: currentConfig.id,
                    restricted_case_ids: newRestrictedIds
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setRestrictedCaseIds(newRestrictedIds);
                // Refresh the docket data to reflect the change
                fetchDocketData(currentConfig.id, pagination.current_page);
            } else {
                alert('Error removing restriction: ' + data.message);
            }
        } catch (error) {
            console.error('Error removing restriction:', error);
            alert('Error removing restriction');
        } finally {
            setLoading(false);
        }
    };

    // Functions for handling checkbox-based case selection for restriction
    const toggleCaseForRestriction = (caseId) => {
        setSelectedCasesForRestriction(prev => {
            if (prev.includes(caseId)) {
                return prev.filter(id => id !== caseId);
            } else {
                return [...prev, caseId];
            }
        });
    };

    const selectAllCasesForRestriction = () => {
        const allCurrentCaseIds = cases.map(case_ => case_.id);
        setSelectedCasesForRestriction(allCurrentCaseIds);
    };

    const deselectAllCasesForRestriction = () => {
        setSelectedCasesForRestriction([]);
    };

    const bulkAddRestrictedCases = async () => {
        if (selectedCasesForRestriction.length === 0) {
            alert('Please select cases to restrict.');
            return;
        }

        // Filter out cases that are already restricted
        const newCasesToRestrict = selectedCasesForRestriction.filter(
            caseId => !restrictedCaseIds.includes(caseId)
        );

        if (newCasesToRestrict.length === 0) {
            alert('All selected cases are already restricted.');
            return;
        }

        const newRestrictedIds = [...restrictedCaseIds, ...newCasesToRestrict];
        
        try {
            setLoading(true);
            const response = await fetch('https://api.casedb.co/docket.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'update_restricted_cases',
                    config_id: currentConfig.id,
                    restricted_case_ids: newRestrictedIds
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setRestrictedCaseIds(newRestrictedIds);
                setSelectedCasesForRestriction([]); // Clear selection after adding
                // Refresh the docket data to reflect the restrictions
                fetchDocketData(currentConfig.id, pagination.current_page);
                alert(`Successfully restricted ${newCasesToRestrict.length} case(s).`);
            } else {
                alert('Error restricting cases: ' + data.message);
            }
        } catch (error) {
            console.error('Error restricting cases:', error);
            alert('Error restricting cases');
        } finally {
            setLoading(false);
        }
    };

    // Townhall link management functions
    const openTownhallModal = () => {
        setEditingTownhallLink(townhallLink || '');
        setEditingTownhallPasscode(townhallPasscode || '');
        setShowTownhallModal(true);
    };

    const saveTownhallLink = async () => {
        if (!currentConfig) return;

        try {
            setLoading(true);
            const response = await fetch('https://api.casedb.co/docket.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'update_townhall_link',
                    config_id: currentConfig.id,
                    townhall_link: editingTownhallLink.trim(),
                    townhall_passcode: editingTownhallPasscode.trim()
                })
            });
            
            const data = await response.json();
            if (data.success) {
                setTownhallLink(editingTownhallLink.trim());
                setTownhallPasscode(editingTownhallPasscode.trim());
                setShowTownhallModal(false);
                alert('Townhall details updated successfully!');
            } else {
                alert('Error updating townhall details: ' + data.message);
            }
        } catch (error) {
            console.error('Error updating townhall details:', error);
            alert('Error updating townhall details');
        } finally {
            setLoading(false);
        }
    };

    const copyTownhallInfo = async () => {
        if (!townhallLink) return;
        
        let textToCopy = townhallLink;
        if (townhallPasscode) {
            textToCopy += `\nPasscode: ${townhallPasscode}`;
        }
        
        try {
            await navigator.clipboard.writeText(textToCopy);
            alert('Townhall details copied to clipboard!');
        } catch (error) {
            console.error('Failed to copy details:', error);
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = textToCopy;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            alert('Townhall details copied to clipboard!');
        }
    };

    const openTownhallLink = () => {
        if (!townhallLink) return;
        window.open(townhallLink, '_blank', 'noopener,noreferrer');
    };

    const goToPage = (page) => {
        if (currentConfig && page >= 1 && page <= pagination.total_pages) {
            fetchDocketData(currentConfig.id, page);
        }
    };

    const goToNextPage = () => {
        if (pagination.has_next) {
            goToPage(pagination.current_page + 1);
        }
    };

    const goToPrevPage = () => {
        if (pagination.has_prev) {
            goToPage(pagination.current_page - 1);
        }
    };

    // Sorting functionality
    const sortCases = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });

        const sortedCases = [...cases].sort((a, b) => {
            let aValue = getCellValue(a, key);
            let bValue = getCellValue(b, key);

            // Handle null/undefined values
            if (aValue === null || aValue === undefined) aValue = '';
            if (bValue === null || bValue === undefined) bValue = '';

            // Convert to appropriate types for comparison
            if (typeof aValue === 'string' && typeof bValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }

            if (aValue < bValue) return direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        setCases(sortedCases);
    };

    const requestSort = (key) => {
        sortCases(key);
    };

    const applyTemplate = (templateKey) => {
        const template = docketTemplates[templateKey];
        if (template) {
            setNewConfig(prev => ({
                ...prev,
                template: templateKey,
                name: template.name,
                description: template.description,
                config: {
                    ...prev.config,
                    filters: template.filters,
                    columns: template.columns
                }
            }));
        }
    };

    const emailTask = async (configId) => {
        try {
            setLoading(true);
            
            // First get the docket data to include in email
            const response = await fetch(`https://api.casedb.co/docket.php?config_id=${configId}&page=1&limit=250&t=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            
            if (!data.success) {
                alert('Failed to fetch docket data for email');
                return;
            }

            // Send email task request
            const emailResponse = await fetch('https://api.casedb.co/docket.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'email_task',
                    config_id: configId,
                    docket_data: data.cases,
                    configuration: data.configuration
                }),
                credentials: 'include'
            });

            const emailData = await emailResponse.json();
            if (emailData.success) {
                alert(`Email task sent successfully! Docket summary emailed to ${emailData.emails_sent} recipients.`);
            } else {
                alert(`Failed to send email task: ${emailData.message}`);
            }
        } catch (error) {
            console.error('Error sending email task:', error);
            alert('Failed to send email task');
        } finally {
            setLoading(false);
        }
    };

    const sendNotifications = async (configId) => {
        try {
            setLoading(true);
            const response = await fetch('https://api.casedb.co/docket.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'send_notifications',
                    config_id: configId
                }),
                credentials: 'include'
            });

            const data = await response.json();
            if (data.success) {
                alert(`Notifications sent successfully to ${data.recipients} recipients for ${data.cases} cases.`);
            } else {
                alert(`Failed to send notifications: ${data.message}`);
            }
        } catch (error) {
            console.error('Error sending notifications:', error);
            alert('Failed to send notifications');
        } finally {
            setLoading(false);
        }
    };

    const handleCellEdit = (caseId, fieldId, value) => {
        updateFieldValue(caseId, fieldId, value);
        setEditingCell(null);
    };

    const handleCellEditImmediate = (caseId, fieldId, value) => {
        updateFieldValue(caseId, fieldId, value);
        // Don't close editing cell immediately for these components
        // They will close on blur or when user clicks elsewhere
    };

    const renderCellValue = (case_, column) => {
        const { field_id, type, editable } = column;
        let value;

        // Get the value from different sources based on field_id
        if (field_id === 'case_name') {
            value = case_.case_name;
        } else if (field_id === 'first_primary_user_name') {
            value = case_.first_primary_user_name || 'N/A';
        } else if (field_id === 'contact_name') {
            value = case_.contact_name || 'N/A';
        } else if (field_id === 'phase_name') {
            value = case_.phase_name || 'N/A';
        } else if (field_id === 'create_date') {
            value = case_.create_date ? new Date(case_.create_date).toLocaleDateString() : 'N/A';
        } else if (field_id === 'created_at') {
            value = case_.created_at ? new Date(case_.created_at).toLocaleDateString() : 'N/A';
        } else {
            value = case_.fields[field_id] || '';
        }

        // Handle editing state
        const cellKey = `${case_.id}-${field_id}`;
        const isEditing = editingCell === cellKey;

        if (isEditing && editable) {
            return (
                <td>
                    <input
                        type={type === 'currency' ? 'number' : 'text'}
                        defaultValue={value}
                        autoFocus
                        onBlur={(e) => handleCellEdit(case_.id, field_id, e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleCellEdit(case_.id, field_id, e.target.value);
                            }
                        }}
                        style={{
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            padding: '4px 8px',
                            backgroundColor: 'var(--background-color)',
                            color: 'var(--text-color)',
                            fontSize: '14px',
                            width: '100%'
                        }}
                    />
                </td>
            );
        }

        // Format display value
        let displayValue = value;
        if (type === 'currency' && value) {
            displayValue = `$${parseFloat(value).toLocaleString()}`;
        }

        return (
            <td
                className={`${editable ? 'editable-cell' : ''} small-text`}
                onClick={() => editable && setEditingCell(cellKey)}
            >
                {field_id === 'case_name' && displayValue ? (
                    <Link 
                        to={`/cases/${case_.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            textWrap: 'wrap',
                            color: 'var(--fill)',
                            cursor: 'pointer',
                            padding: '5px 10px',
                            textDecoration: 'none',
                            width: '100%'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.textDecoration = 'underline';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.textDecoration = 'none';
                        }}
                    >
                        {displayValue}
                    </Link>
                ) : (
                    (displayValue !== null && displayValue !== undefined && displayValue !== '') ? displayValue : (editable ? <span style={{ color: 'var(--subtext-color)', fontStyle: 'italic', padding: '4px 8px', borderRadius: '6px' }}>Click to edit</span> : 'N/A')
                )}
                {editable && <Edit3 size={12} style={{ color: 'var(--subtext-color)' }} />}
            </td>
        );
    };

    const CreateConfigModal = () => (
        <Modal
            title="Create New Docket"
            instructions="Choose a template and configure your docket settings"
            onClose={() => setShowCreateModal(false)}
            wide={true}
            footer={
                <div className='modal-footer-actions'>
                    <button 
                        className="action" 
                        onClick={createConfiguration}
                        disabled={!newConfig.name || loading}
                    >
                        {loading ? 'Creating...' : 'Create Docket'}
                    </button>
                    <button 
                        className="action alt" 
                        onClick={() => setShowCreateModal(false)}
                    >
                        Cancel
                    </button>
                </div>
            }
        >
            <div className='modal-content-wrapper'>
                <label className="subtext" style={{ marginBottom: '2.5px' }}>Choose a Template</label>
                <div className="template-grid" style={{ display: 'grid', gap: '12px', width: '100%'}}>
                    <div 
                        className={`template-card ${newConfig.template === 'settlement_review' ? 'selected' : ''}`}
                        onClick={() => applyTemplate('settlement_review')}
                        style={{
                            padding: '16px',
                            border: `2px solid ${newConfig.template === 'settlement_review' ? '#4CAF50' : 'var(--border-color)'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: newConfig.template === 'settlement_review' ? 'rgba(76, 175, 80, 0.1)' : 'var(--secondary-color)',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-color)', fontSize: '16px' }}>Settlement Review</h4>
                        <p style={{ margin: '0', fontSize: '14px', color: 'var(--subtext-color)' }}>
                            Cases assigned to me that need projected settlement values
                        </p>
                    </div>
                    
                    <div 
                        className={`template-card ${newConfig.template === 'case_vitals' ? 'selected' : ''}`}
                        onClick={() => applyTemplate('case_vitals')}
                        style={{
                            padding: '16px',
                            border: `2px solid ${newConfig.template === 'case_vitals' ? '#4CAF50' : 'var(--border-color)'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: newConfig.template === 'case_vitals' ? 'rgba(76, 175, 80, 0.1)' : 'var(--secondary-color)',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-color)', fontSize: '16px' }}>My Case Vitals</h4>
                        <p style={{ margin: '0', fontSize: '14px', color: 'var(--subtext-color)' }}>
                            All active cases where I'm on the team - basic information
                        </p>
                    </div>
                    
                    <div 
                        className={`template-card ${newConfig.template === 'custom' ? 'selected' : ''}`}
                        onClick={() => applyTemplate('custom')}
                        style={{
                            padding: '16px',
                            border: `2px solid ${newConfig.template === 'custom' ? '#4CAF50' : 'var(--border-color)'}`,
                            borderRadius: '8px',
                            cursor: 'pointer',
                            backgroundColor: newConfig.template === 'custom' ? 'rgba(76, 175, 80, 0.1)' : 'var(--secondary-color)',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        <h4 style={{ margin: '0 0 8px 0', color: 'var(--text-color)', fontSize: '16px' }}>Custom Docket</h4>
                        <p style={{ margin: '0', fontSize: '14px', color: 'var(--subtext-color)' }}>
                            Start with a basic template and customize
                        </p>
                    </div>
                </div>

                <div className="form-group full" style={{ width: '100%', maxWidth: '100%' }}>
                    <label className="subtext">Docket Name</label>
                    <input
                        type="text"
                        value={newConfig.name}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter a name for your docket"
                    />
                </div>
                
                <div className="form-group full" style={{ width: '100%', maxWidth: '100%' }}>
                    <label className="subtext">Description</label>
                    <textarea
                        value={newConfig.description}
                        onChange={(e) => setNewConfig(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="What is this docket used for?"
                        rows={2}
                    />
                </div>

                {newConfig.template && (
                <div className="form-group full" style={{ width: '100%', maxWidth: '100%' }}>
                        <label className="subtext">Template Details</label>
                        <div className="template-preview" style={{ 
                            padding: '16px', 
                            backgroundColor: 'var(--secondary-color)', 
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            fontSize: '14px',
                            width: '100%'
                        }}>
                            <div style={{ marginBottom: '8px', color: 'var(--text-color)' }}>
                                <strong>Shows cases where:</strong> <span style={{ color: 'var(--subtext-color)' }}>{
                                    newConfig.config.filters.team_position === 'first_primary' 
                                        ? 'I am the primary attorney'
                                        : 'I am anywhere on the team'
                                }</span>
                            </div>
                            <div style={{ marginBottom: '8px', color: 'var(--text-color)' }}>
                                <strong>Columns:</strong> <span style={{ color: 'var(--subtext-color)' }}>{newConfig.config.columns.map(col => col.label).join(', ')}</span>
                            </div>
                            {newConfig.config.filters.field_conditions?.length > 0 && (
                                <div style={{ color: 'var(--text-color)' }}>
                                    <strong>Special filters:</strong> <span style={{ color: 'var(--subtext-color)' }}>{
                                        newConfig.config.filters.field_conditions.map(fc => 
                                            fc.field_id === 194 ? 'Missing settlement values' : 'Custom filter'
                                        ).join(', ')
                                    }</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                <Checkbox
                    value={newConfig.config.notifications?.enabled}
                    onChange={() => setNewConfig(prev => ({
                        ...prev,
                        config: {
                            ...prev.config,
                            notifications: {
                                ...prev.config.notifications,
                                enabled: !prev.config.notifications?.enabled
                            }
                        }
                    }))}
                    label={'Send me weekly email reminders'}
                />
            </div>
        </Modal>
    );

    const TownhallModal = () => (
        <Modal
            title="Townhall Details"
            instructions="Add or update the townhall meeting details for this docket"
            onClose={() => setShowTownhallModal(false)}
            footer={
                <div className='modal-footer-actions'>
                    <button 
                        className="action" 
                        onClick={saveTownhallLink}
                        disabled={loading}
                    >
                        {loading ? 'Saving...' : 'Save Details'}
                    </button>
                    <button 
                        className="action alt" 
                        onClick={() => setShowTownhallModal(false)}
                    >
                        Cancel
                    </button>
                </div>
            }
        >
            <div className='modal-content-wrapper'>
                <div className='form-group full'>
                    <label className="subtext">Meeting Link (Zoom, Teams, etc.)</label>
                    <input
                        type="url"
                        value={editingTownhallLink}
                        onChange={(e) => setEditingTownhallLink(e.target.value)}
                        placeholder="https://zoom.us/j/123456789 or https://teams.microsoft.com/..."
                        style={{ width: '100%' }}
                    />
                </div>

                <div className='form-group full'>
                    <label className="subtext">Meeting Passcode (Optional)</label>
                    <input
                        type="text"
                        value={editingTownhallPasscode}
                        onChange={(e) => setEditingTownhallPasscode(e.target.value)}
                        placeholder="Enter meeting passcode if required"
                        style={{ width: '100%' }}
                    />
                    <div style={{ 
                        fontSize: '12px', 
                        color: 'var(--subtext-color)', 
                        marginTop: '8px' 
                    }}>
                        These details will be available to all docket users and can be copied together.
                    </div>
                </div>
                
                {(townhallLink || townhallPasscode) && (
                    <div style={{
                        marginTop: '15px',
                        padding: '12px',
                        backgroundColor: 'var(--secondary-color)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '6px'
                    }}>
                        <div style={{ 
                            fontSize: '12px', 
                            color: 'var(--subtext-color)', 
                            marginBottom: '8px',
                            fontWeight: '500'
                        }}>
                            Current Details:
                        </div>
                        {townhallLink && (
                            <div style={{ marginBottom: '6px' }}>
                                <div style={{ 
                                    fontSize: '11px', 
                                    color: 'var(--subtext-color)', 
                                    marginBottom: '2px' 
                                }}>
                                    Link:
                                </div>
                                <div style={{ 
                                    fontSize: '14px', 
                                    color: 'var(--text-color)',
                                    wordBreak: 'break-all',
                                    fontFamily: 'monospace'
                                }}>
                                    {townhallLink}
                                </div>
                            </div>
                        )}
                        {townhallPasscode && (
                            <div>
                                <div style={{ 
                                    fontSize: '11px', 
                                    color: 'var(--subtext-color)', 
                                    marginBottom: '2px' 
                                }}>
                                    Passcode:
                                </div>
                                <div style={{ 
                                    fontSize: '14px', 
                                    color: 'var(--text-color)',
                                    fontFamily: 'monospace'
                                }}>
                                    {townhallPasscode}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </Modal>
    );

    return (
        <div className={`page-container docket-page ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div id="page-header" style={{ "justifyContent": 'space-between' }}>
                <h1 style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: "25px" }}>
                    {currentConfig ? currentConfig.name : 'Docket Management'}
                    <div className="subtext">
                        {currentConfig ? (
                            <div className='docket-description' style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
                                <span>{currentConfig.description}</span>
                                {(() => {
                                    const urlParams = new URLSearchParams(window.location.search);
                                    const userParam = urlParams.get('user');
                                    if (userParam && currentUser && parseInt(userParam) !== currentUser.id) {
                                        return (
                                            <span style={{
                                                padding: '5px 10px',
                                                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                                                border: '1px solid rgba(76, 175, 80, 0.3)',
                                                borderRadius: '6px',
                                                color: '#4CAF50',
                                                fontWeight: '500'
                                            }}>
                                                Viewing docket for User ID: {userParam}
                                            </span>
                                        );
                                    }
                                    return null;
                                })()}
                            </div>
                        ) : (
                            'Create and manage custom docket configurations for different workflows. Set up automated notifications and easily track case progress.'
                        )}
                    </div>
                </h1>
                {currentConfig ? (
                    <button className="action alt" onClick={() => setCurrentConfig(null)}>
                        <ArrowLeft size={16} /> Back to Configurations
                    </button>
                ) : (
                    /*<button className="action" onClick={() => setShowCreateModal(true)}>
                        <Plus size={16} />
                        Create Docket View
                    </button>*/
                    <></>
                )}
            </div>

            {!currentConfig ? (
                <div className="docket-home">
                    {loading ? (
                        <div className="loading">Loading configurations...</div>
                    ) : (
                        <div className="cases-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))' }}>
                            {configurations.map(config => (
                                <ConfigurationCard 
                                    key={config.id} 
                                    config={config} 
                                    onSelect={fetchDocketData}
                                />
                            ))}
                            
                            {configurations.length === 0 && (
                                <div className="empty-state" style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px' }}>
                                    <Calendar size={48} style={{ color: 'var(--subtext-color)', marginBottom: '16px' }} />
                                    <h3 style={{ color: 'var(--text-color)', marginBottom: '8px' }}>No Docket Configurations</h3>
                                    <p style={{ color: 'var(--subtext-color)', marginBottom: '24px' }}>Create your first docket configuration to get started.</p>
                                    <button className="action" onClick={() => setShowCreateModal(true)}>
                                        <Plus size={16} />
                                        Create Your First Docket
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="docket-view">
                    <div className="docket-header">
                        {console.log('Debug docket header:', { 
                            currentUser: currentUser, 
                            currentConfig: currentConfig,
                            userIdMatch: currentUser?.id === currentConfig?.created_by,
                            currentUserId: currentUser?.id,
                            configCreatedBy: currentConfig?.created_by
                        })}
                        {currentUser && currentUser.id === currentConfig.created_by && (
                            <div className="docket-actions" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: '20px', justifyContent: "center" }}>
                                <button className="action alt">
                                    <Settings size={16} />
                                    Configure
                                </button>
                                <button 
                                    className="action alt" 
                                    onClick={() => {
                                        console.log('Restrict button clicked, showRestrictCases:', showRestrictCases);
                                        const newShowRestrict = !showRestrictCases;
                                        setShowRestrictCases(newShowRestrict);
                                        // Clear selection when hiding restriction mode
                                        if (!newShowRestrict) {
                                            setSelectedCasesForRestriction([]);
                                        }
                                    }}
                                >
                                    <Filter size={16} />
                                    {showRestrictCases ? 'Hide' : 'Restrict'} Cases
                                </button>
                                <button 
                                    className="action alt"
                                    onClick={openTownhallModal}
                                >
                                    <Link2 size={16} />
                                    Townhall Details
                                </button>
                                {townhallLink && (
                                    <>
                                        <button 
                                            className="action alt"
                                            onClick={copyTownhallInfo}
                                            title="Copy townhall details"
                                        >
                                            <Copy size={16} />
                                            Copy Details
                                        </button>
                                        <button 
                                            className="action"
                                            onClick={openTownhallLink}
                                            title="Open townhall link"
                                        >
                                            <ExternalLink size={16} />
                                            Join Townhall
                                        </button>
                                    </>
                                )}
                                <button 
                                    className="action alt" 
                                    onClick={() => emailTask(currentConfig.id)}
                                    disabled={loading}
                                >
                                    <Mail size={16} />
                                    {loading ? 'Sending...' : 'Email Task'}
                                </button>
                                <button 
                                    className="action" 
                                    onClick={() => sendNotifications(currentConfig.id)}
                                    disabled={loading}
                                >
                                    <Calendar size={16} />
                                    {loading ? 'Sending...' : 'Send Notifications'}
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Case Restriction Interface */}
                    {console.log('Debug case restriction panel:', { 
                        showRestrictCases, 
                        currentUser: currentUser, 
                        currentConfig: currentConfig,
                        userIdMatch: currentUser?.id === currentConfig?.created_by,
                        shouldShow: showRestrictCases && currentUser && currentUser.id === currentConfig.created_by
                    })}
                    {showRestrictCases && currentUser && currentUser.id === currentConfig.created_by && (
                        <div className="case-restriction-panel" style={{
                            backgroundColor: 'var(--secondary-color)',
                            border: '1px solid var(--border-color)',
                            borderRadius: '12px',
                            padding: '20px',
                            marginBottom: '20px'
                        }}>
                            <h4 style={{ margin: '0 0 15px 0', color: 'var(--text-color)' }}>Restrict Cases from Docket</h4>
                            <p style={{ margin: '0 0 15px 0', color: 'var(--subtext-color)', fontSize: '14px' }}>
                                Select cases to exclude from this docket. Restricted cases will not appear in the results.
                            </p>
                            
                            <div className="form-group" style={{ marginBottom: '15px' }}>
                                <SearchSelect
                                    onChange={(value) => {
                                        addRestrictedCase(value);
                                    }}
                                    options={availableCases}
                                    loadOptions={loadCaseOptions}
                                    loadOptionsMinChars={2}
                                    placeholder="Search and select a case to restrict..."
                                />
                            </div>

                            {/* Bulk Selection Controls */}
                            <div style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '10px', 
                                marginBottom: '15px',
                                padding: '12px',
                                backgroundColor: 'var(--background-color)',
                                border: '1px solid var(--border-color)',
                                borderRadius: '8px'
                            }}>
                                <span style={{ color: 'var(--text-color)', fontSize: '14px', fontWeight: '500' }}>
                                    Bulk Actions:
                                </span>
                                <button 
                                    className="action alt"
                                    onClick={selectAllCasesForRestriction}
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                >
                                    Select All
                                </button>
                                <button 
                                    className="action alt"
                                    onClick={deselectAllCasesForRestriction}
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                >
                                    Clear Selection
                                </button>
                                <button 
                                    className="action"
                                    onClick={bulkAddRestrictedCases}
                                    disabled={selectedCasesForRestriction.length === 0 || loading}
                                    style={{ fontSize: '12px', padding: '4px 8px' }}
                                >
                                    {loading ? 'Adding...' : `Restrict Selected (${selectedCasesForRestriction.length})`}
                                </button>
                                {selectedCasesForRestriction.length > 0 && (
                                    <span style={{ color: 'var(--subtext-color)', fontSize: '12px' }}>
                                        {selectedCasesForRestriction.length} case(s) selected
                                    </span>
                                )}
                            </div>

                            {restrictedCaseIds.length > 0 && (
                                <div>
                                    <h5 style={{ margin: '0 0 10px 0', color: 'var(--text-color)' }}>
                                        Currently Restricted Cases ({restrictedCaseIds.length})
                                    </h5>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {restrictedCaseIds.map(caseId => {
                                            const caseName = availableCases[caseId] || `Case #${caseId}`;
                                            return (
                                                <div 
                                                    key={caseId}
                                                    style={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        backgroundColor: 'var(--border-color)',
                                                        padding: '4px 8px',
                                                        borderRadius: '6px',
                                                        fontSize: '12px',
                                                        color: 'var(--text-color)'
                                                    }}
                                                >
                                                    <span>{caseName}</span>
                                                    <button
                                                        onClick={() => removeRestrictedCase(caseId)}
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: 'var(--subtext-color)',
                                                            cursor: 'pointer',
                                                            padding: '0',
                                                            display: 'flex',
                                                            alignItems: 'center'
                                                        }}
                                                        title="Remove restriction"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {loading ? (
                        <div className="loading">Loading docket data...</div>
                    ) : (
                        <div className="docket-view-content" style={{ display: "flex", flexDirection: "column", gap: "20px", justifyContent: "flex-start", alignItems: "center", width: "100%" }}>
                            <div className="docket-content">
                                <div className="data twofive-twofive">
                                    <div className="data-label">
                                        <span>Total Cases</span>
                                    </div>
                                    <span className="data-value">{pagination.total_cases}</span>
                                </div>
                                <div className="data one-one">
                                    <div className="data-label">
                                        <span>Showing</span>
                                    </div>
                                    <span className="data-value">{cases.length} cases</span>
                                </div>
                                <div className="data one-one">
                                    <div className="data-label">
                                        <span>Page</span>
                                    </div>
                                    <span className="data-value">{pagination.current_page} of {pagination.total_pages}</span>
                                </div>
                                {townhallLink && <div className='data one-one'>
                                    <div className="data-label">
                                        <span>Townhall Link</span>
                                    </div>
                                    <span className="data-value subtext" style={{ textWrap: 'wrap' }}>{townhallLink}</span>
                                    <span className="data-value subtext" style={{ marginTop: '10px' }}>Passcode: {townhallPasscode}</span>
                                </div>}
                            </div>

                            <div className="report-table-container">
                                <table 
                                    className="report-table docket-table" 
                                    style={{ 
                                        tableLayout: 'fixed',
                                        width: getTotalTableWidth(currentConfig.config.columns, cases, showRestrictCases && currentUser && currentUser.id === currentConfig.created_by)
                                    }}
                                >
                                    <thead className="report-sticky-header">
                                        <tr>
                                            {showRestrictCases && currentUser && currentUser.id === currentConfig.created_by && (
                                                <th 
                                                    className="report-sortable-header docket-sortable-header"
                                                    style={{ 
                                                        width: '60px',
                                                        minWidth: '60px',
                                                        textAlign: 'center'
                                                    }}
                                                >
                                                    <div className="header-content">
                                                        <Checkbox
                                                            value={selectedCasesForRestriction.length === cases.length && cases.length > 0}
                                                            onChange={(checked) => {
                                                                if (checked) {
                                                                    selectAllCasesForRestriction();
                                                                } else {
                                                                    deselectAllCasesForRestriction();
                                                                }
                                                            }}
                                                            label=""
                                                            title="Select all cases"
                                                        />
                                                    </div>
                                                </th>
                                            )}
                                            {currentConfig.config.columns.map(column => (
                                                <th 
                                                    key={column.field_id}
                                                    className="report-sortable-header docket-sortable-header"
                                                    onClick={() => requestSort(column.field_id)}
                                                    style={{ 
                                                        width: getColumnWidth(column, cases),
                                                        minWidth: getColumnWidth(column, cases),
                                                        textAlign: getTextAlign(column)
                                                    }}
                                                >
                                                    <div className="header-content">
                                                        <span>
                                                            {column.label}
                                                            {column.editable && <Edit3 size={12} style={{ marginLeft: '8px', color: 'var(--subtext-color)' }} />}
                                                        </span>
                                                        <span className="sort-icon">
                                                            {getSortIcon(column, sortConfig)}
                                                        </span>
                                                    </div>
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cases.map((case_, index) => (
                                            <tr key={case_.id} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                                                {showRestrictCases && currentUser && currentUser.id === currentConfig.created_by && (
                                                    <td 
                                                        className="report-cell"
                                                        style={{
                                                            width: '60px',
                                                            minWidth: '60px',
                                                            textAlign: 'center',
                                                            padding: '8px'
                                                        }}
                                                    >
                                                        <Checkbox
                                                            value={selectedCasesForRestriction.includes(case_.id)}
                                                            onChange={(checked) => toggleCaseForRestriction(case_.id)}
                                                            label=""
                                                            disabled={restrictedCaseIds.includes(case_.id)}
                                                            title={restrictedCaseIds.includes(case_.id) ? 'Case already restricted' : 'Select case for restriction'}
                                                        />
                                                    </td>
                                                )}
                                                {currentConfig.config.columns.map(column => {
                                                    const cellValue = getCellValue(case_, column.field_id);
                                                    const formattedValue = formatCellValue(cellValue, column.type);
                                                    
                                                    return (
                                                        <td
                                                            key={`${case_.id}-${column.field_id}`}
                                                            className={`report-cell docket-editable-cell ${column.field_id === 'case_name' ? 'docket-case-link' : ''} ${column.editable ? 'editable' : ''}`}
                                                            style={{
                                                                width: getColumnWidth(column, cases),
                                                                minWidth: getColumnWidth(column, cases),
                                                                textAlign: getTextAlign(column),
                                                                overflow: 'hidden',
                                                                textOverflow: 'ellipsis',
                                                                whiteSpace: 'nowrap',
                                                                cursor: column.editable ? 'pointer' : 'default'
                                                            }}
                                                            title={String(formattedValue || '')} // Tooltip for overflow content
                                                            onClick={() => {
                                                                if (column.editable) {
                                                                    const cellKey = `${case_.id}-${column.field_id}`;
                                                                    setEditingCell(cellKey);
                                                                }
                                                            }}
                                                        >
                                                            {editingCell === `${case_.id}-${column.field_id}` && column.editable ? (

                                                                <div className='form-group nm full'>
                                                                    {column.type === 'date' ? (
                                                                        <DateInput
                                                                            value={cellValue}
                                                                            onChange={(newValue) => {
                                                                                handleCellEdit(case_.id, column.field_id, newValue);
                                                                            }}
                                                                        />
                                                                    ) : column.type === 'dropdown' || column.type === 'list' ? (
                                                                        (() => {
                                                                            const options = fieldOptions[column.field_id] || [];
                                                                            // Convert display value back to index for dropdown component
                                                                            const currentIndex = options.indexOf(cellValue);
                                                                            
                                                                            console.log(`Editing ${column.type} field:`, {
                                                                                field_id: column.field_id,
                                                                                options: options,
                                                                                optionsLength: options?.length,
                                                                                currentValue: cellValue,
                                                                                currentIndex: currentIndex
                                                                            });
                                                                            
                                                                            return (
                                                                                <Dropdown
                                                                                    value={currentIndex >= 0 ? currentIndex : null}
                                                                                    options={options}
                                                                                    onChange={(selectedIndex) => {
                                                                                        // Convert index back to display value for storage
                                                                                        const selectedValue = options[selectedIndex];
                                                                                        handleCellEdit(case_.id, column.field_id, selectedIndex);
                                                                                    }}
                                                                                    onBlur={() => setEditingCell(null)}
                                                                                    placeholder="Select an option"
                                                                                />
                                                                            );
                                                                        })()
                                                                    ) : column.type === 'searchselect' ? (
                                                                        <SearchSelect
                                                                            value={cellValue}
                                                                            options={fieldOptions[column.field_id] || {}}
                                                                            onChange={(newValue) => {
                                                                                handleCellEdit(case_.id, column.field_id, newValue);
                                                                            }}
                                                                            placeholder="Search and select"
                                                                        />
                                                                    ) : column.type === 'textarea' ? (
                                                                        <textarea
                                                                            defaultValue={cellValue}
                                                                            autoFocus
                                                                            onBlur={(e) => handleCellEdit(case_.id, column.field_id, e.target.value)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter' && e.ctrlKey) {
                                                                                    handleCellEdit(case_.id, column.field_id, e.target.value);
                                                                                }
                                                                                if (e.key === 'Escape') {
                                                                                    setEditingCell(null);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                border: '1px solid var(--border-color)',
                                                                                borderRadius: '4px',
                                                                                padding: '4px 8px',
                                                                                backgroundColor: 'var(--background-color)',
                                                                                color: 'var(--text-color)',
                                                                                fontSize: '14px',
                                                                                width: '100%',
                                                                                outline: 'none',
                                                                                minHeight: '60px',
                                                                                resize: 'vertical'
                                                                            }}
                                                                        />
                                                                    ) : column.type === 'boolean' ? (
                                                                        <Checkbox
                                                                            value={cellValue === 'true' || cellValue === true || cellValue === 1}
                                                                            onChange={(checked) => {
                                                                                handleCellEdit(case_.id, column.field_id, checked);
                                                                            }}
                                                                            label=""
                                                                        />
                                                                    ) : (
                                                                        <input
                                                                            type={column.type === 'currency' || column.type === 'number' ? 'number' : 'text'}
                                                                            defaultValue={cellValue}
                                                                            autoFocus
                                                                            onBlur={(e) => handleCellEdit(case_.id, column.field_id, e.target.value)}
                                                                            onKeyDown={(e) => {
                                                                                if (e.key === 'Enter') {
                                                                                    handleCellEdit(case_.id, column.field_id, e.target.value);
                                                                                }
                                                                                if (e.key === 'Escape') {
                                                                                    setEditingCell(null);
                                                                                }
                                                                            }}
                                                                            style={{
                                                                                border: '1px solid var(--border-color)',
                                                                                borderRadius: '4px',
                                                                                padding: '4px 8px',
                                                                                backgroundColor: 'var(--background-color)',
                                                                                color: 'var(--text-color)',
                                                                                fontSize: '14px',
                                                                                width: '100%',
                                                                                outline: 'none'
                                                                            }}
                                                                        />
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <div style={{ 
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap'
                                                                }}>
                                                                    {column.field_id === 'case_name' && formattedValue ? (
                                                                        <Link 
                                                                            to={`/case/${case_.id}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            className="docket-case-link"
                                                                            style={{
                                                                                color: 'var(--fill)',
                                                                                textDecoration: 'none',
                                                                                display: 'block',
                                                                                width: '100%',
                                                                                overflow: 'hidden',
                                                                                textOverflow: 'ellipsis',
                                                                                whiteSpace: 'nowrap'
                                                                            }}
                                                                            onMouseEnter={(e) => {
                                                                                e.target.style.textDecoration = 'underline';
                                                                            }}
                                                                            onMouseLeave={(e) => {
                                                                                e.target.style.textDecoration = 'none';
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()} // Prevent cell edit when clicking link
                                                                        >
                                                                            {formattedValue}
                                                                        </Link>
                                                                    ) : (
                                                                        <span>
                                                                            {(formattedValue !== null && formattedValue !== undefined && formattedValue !== '') ? formattedValue : (column.editable ? <span style={{ color: 'var(--subtext-color)', fontStyle: 'italic' }}>Click to edit</span> : 'N/A')}
                                                                        </span>
                                                                    )}
                                                                    {column.editable && <Edit3 size={12} title="Click to edit" style={{ color: 'var(--subtext-color)', marginLeft: formattedValue ? '4px' : '0' }} />}
                                                                </div>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {cases.length === 0 && (
                                <div style={{ 
                                    textAlign: 'center', 
                                    padding: '40px',
                                    backgroundColor: 'var(--secondary-color)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '12px'
                                }}>
                                    <Eye size={48} style={{ color: 'var(--subtext-color)', marginBottom: '16px' }} />
                                    <h3 style={{ color: 'var(--text-color)', margin: '0 0 8px 0' }}>No Cases Found</h3>
                                    <p style={{ color: 'var(--subtext-color)', margin: '0' }}>No cases match the current docket configuration filters.</p>
                                </div>
                            )}

                            {/* Pagination Controls */}
                            {pagination.total_pages > 1 && (
                                <div className="pagination-container" style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: '20px',
                                    padding: '16px',
                                    backgroundColor: 'var(--secondary-color)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '12px'
                                }}>
                                    <div className="pagination-info" style={{ color: 'var(--text-color)' }}>
                                        Showing {((pagination.current_page - 1) * pagination.per_page) + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total_cases)} of {pagination.total_cases} cases
                                    </div>
                                    
                                    <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                        <button 
                                            className="action alt" 
                                            onClick={goToPrevPage}
                                            disabled={!pagination.has_prev}
                                            style={{ opacity: pagination.has_prev ? 1 : 0.5 }}
                                        >
                                            ← Previous
                                        </button>
                                        
                                        <div style={{ display: 'flex', gap: '4px' }}>
                                            {/* Page numbers */}
                                            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                                                let pageNum;
                                                if (pagination.total_pages <= 5) {
                                                    pageNum = i + 1;
                                                } else if (pagination.current_page <= 3) {
                                                    pageNum = i + 1;
                                                } else if (pagination.current_page >= pagination.total_pages - 2) {
                                                    pageNum = pagination.total_pages - 4 + i;
                                                } else {
                                                    pageNum = pagination.current_page - 2 + i;
                                                }
                                                
                                                return (
                                                    <button
                                                        key={pageNum}
                                                        className={pageNum === pagination.current_page ? "action" : "action alt"}
                                                        onClick={() => goToPage(pageNum)}
                                                        style={{ 
                                                            minWidth: '40px',
                                                            padding: '8px 12px'
                                                        }}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        
                                        <button 
                                            className="action alt" 
                                            onClick={goToNextPage}
                                            disabled={!pagination.has_next}
                                            style={{ opacity: pagination.has_next ? 1 : 0.5 }}
                                        >
                                            Next →
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {showCreateModal && <CreateConfigModal />}
            {showTownhallModal && <TownhallModal />}
        </div>
    );
};
